import I18n from 'i18n-js';
import { useEffect, useState, useCallback } from 'react';
import { Card, Container, Spinner, Table } from 'react-bootstrap';
import { DateTime } from 'luxon';
import { useAppSelector } from '../../hooks';
import { LoadingState } from '../../shared/interface';
import { getUpcomingBookings, UpcomingBookingDto } from '../../service/upcoming-bookings';
import './UpcomingBookings.scss';

export const UpcomingBookings = () => {
    const tenantId = useAppSelector(state => state.settings.tenantId);
    const routeId = useAppSelector(state => state.settings.routeId);
    const [bookings, setBookings] = useState<UpcomingBookingDto[]>([]);
    const [loading, setLoading] = useState<LoadingState>(LoadingState.IDLE);
    const [error, setError] = useState<string | null>(null);

    const fetchUpcomingBookings = useCallback(async () => {
        setLoading(LoadingState.PENDING);
        setError(null);

        try {
            // Use the actual API service
            const data = await getUpcomingBookings({
                fromDate: DateTime.now().toFormat('yyyy-MM-dd'),
                toDate: DateTime.now().plus({ days: 14 }).toFormat('yyyy-MM-dd'),
                routeId: routeId || undefined  // Pass the routeId if it exists
            });

            setBookings(data);
            setLoading(LoadingState.COMPLETE);
        } catch (err) {
            console.error('Failed to load upcoming bookings:', err);
            setError(I18n.t('DefaultErrorMessage'));
            setLoading(LoadingState.FAILED);
        }
    }, [routeId]);

    useEffect(() => {
        if (tenantId) {
            fetchUpcomingBookings();
        }
    }, [tenantId, fetchUpcomingBookings]);

    const groupBookingsByDate = (bookings: UpcomingBookingDto[]) => {
        return bookings.reduce((groups, booking) => {
            const date = booking.date;
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(booking);
            return groups;
        }, {} as Record<string, UpcomingBookingDto[]>);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('sv-SE', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };


    if (loading === LoadingState.PENDING) {
        return (
            <Container fluid className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                    <h4 className="mt-3">{I18n.t('::LoadingWidget')}</h4>
                </div>
            </Container>
        );
    }

    if (loading === LoadingState.FAILED) {
        return (
            <Container fluid>
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">{I18n.t('Error')}</h4>
                    <p>{error}</p>
                </div>
            </Container>
        );
    }

    const groupedBookings = groupBookingsByDate(bookings);
    // Get route name from the first booking (all bookings should have the same route name)
    const routeName = bookings.length > 0 ? bookings[0].routeName : null;
    return (
         <Container fluid className="upcoming-bookings-container">
            <div className="bg-dark py-5">
                <h1 className="display-4 fw-bold text-light text-center">
                    {routeName}
                </h1>
                <p className="text-light text-center mb-0">
                    {DateTime.now().toFormat('yyyy-MM-dd')} - {DateTime.now().plus({ days: 14 }).toFormat('yyyy-MM-dd')}
                </p>
            </div>

            {Object.keys(groupedBookings).length === 0 ? (
                <div className="text-center">
                    <h3>{I18n.t('NoDataAvailableInDatatable')}</h3>
                    <p className="text-muted">{I18n.t('::NoUpcomingBookings')}</p>
                </div>
            ) : (
                Object.entries(groupedBookings).map(([date, dayBookings]) => (
                    <Card key={date} className="my-3">
                        <Card.Header className="d-flex justify-content-between">
                            <h5 className="mb-0">{formatDate(date)}</h5>
                        </Card.Header>
                        <Card.Body >
                            <div className="table-responsive">
                                <Table striped className="w-100">
                                    <thead>
                                        <tr>
                                            <th className="ps-3">{I18n.t('::Route')}</th>
                                            <th>{I18n.t('::From')}</th>
                                            <th>{I18n.t('::To')}</th>
                                            <th>{I18n.t('::Departure')}</th>
                                            <th>{I18n.t('::Arrival2')}</th>
                                            <th className="ps-3">{I18n.t('::Passengers')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dayBookings.map((booking) => {
                                            return (
                                                <tr key={booking.id}>
                                                    <td className="ps-3">{booking.routeName}</td>
                                                    <td>{booking.departureHarbour}</td>
                                                    <td>{booking.arrivalHarbour}</td>
                                                    <td>{booking.departureTime}</td>
                                                    <td>{booking.arrivalTime}</td>
                                                    <td className="ps-3">{booking.bookedSeats}/{booking.capacity}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                ))
            )}
        </Container>
    );
};
